import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, FileText, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEXICAN_STATES = [
  'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Chiapas',
  'Chihuahua', 'Ciudad de México', 'Coahuila', 'Colima', 'Durango',
  'Estado de México', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco',
  'Michoacán', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca',
  'Puebla', 'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa',
  'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala', 'Veracruz', 'Yucatán', 'Zacatecas',
];

const CFDI_USES = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'P01', label: 'P01 - Por definir' },
];

const InvoiceRequest = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invoiceInfo, setInvoiceInfo] = useState(null);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    type: 'persona_fisica',
    name: '',
    rfc: '',
    email: '',
    country: 'México',
    state: '',
    street: '',
    neighborhood: '',
    postal_code: '',
    city: '',
    cfdi_use: '',
  });

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}/invoice`);
        setInvoiceInfo(res.data);
        if (res.data.invoice_data) {
          setForm(prev => ({ ...prev, ...res.data.invoice_data }));
        }
      } catch {
        toast.error('Orden no encontrada');
        navigate('/');
      }
      setLoading(false);
    };
    fetchInvoice();
  }, [orderId, navigate]);

  const update = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Obligatorio';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = 'Correo inválido';
    if (!form.state) e.state = 'Obligatorio';
    if (!form.street.trim()) e.street = 'Obligatorio';
    if (!form.postal_code.trim()) e.postal_code = 'Obligatorio';
    if (!form.city.trim()) e.city = 'Obligatorio';

    const rfc = form.rfc.trim().toUpperCase();
    const expectedLen = form.type === 'persona_fisica' ? 13 : 12;
    if (!rfc || rfc.length !== expectedLen) {
      e.rfc = `El RFC debe tener ${expectedLen} caracteres para ${form.type === 'persona_fisica' ? 'persona física' : 'empresa'}`;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/orders/${orderId}/invoice`, { ...form, rfc: form.rfc.toUpperCase() });
      toast.success('Solicitud de factura enviada');
      // Refresh
      const res = await axios.get(`${API}/orders/${orderId}/invoice`);
      setInvoiceInfo(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al solicitar factura');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const FieldError = ({ field }) => errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

  const alreadyRequested = invoiceInfo?.invoice_requested;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <span className="text-sm text-muted-foreground">Facturación</span>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6 gap-2" data-testid="btn-back-home">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Button>

        {alreadyRequested ? (
          <Card className="shadow-soft" data-testid="invoice-already-requested">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold mb-2">Factura solicitada</h2>
              <p className="text-muted-foreground mb-4">
                Ya solicitaste factura para el pedido <strong>{invoiceInfo.order_number}</strong>.
              </p>
              <div className="bg-muted/50 rounded-lg p-4 text-sm text-left space-y-1 mb-4">
                <p><span className="text-muted-foreground">RFC:</span> <strong>{invoiceInfo.invoice_data?.rfc}</strong></p>
                <p><span className="text-muted-foreground">Razón social:</span> <strong>{invoiceInfo.invoice_data?.name}</strong></p>
                <p><span className="text-muted-foreground">Status:</span> <strong className="capitalize">{invoiceInfo.invoice_status === 'pending' ? 'Pendiente' : invoiceInfo.invoice_status === 'generated' ? 'Generada' : invoiceInfo.invoice_status === 'sent' ? 'Enviada' : invoiceInfo.invoice_status}</strong></p>
              </div>
              <p className="text-xs text-muted-foreground">
                Si necesitas hacer cambios, contacta a <a href="mailto:hola@kahamx.com" className="text-accent underline">hola@kahamx.com</a>
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-soft">
            <CardHeader className="border-b bg-[#58655A] text-white">
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Solicitar factura
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5" data-testid="invoice-form">
              <p className="text-sm text-muted-foreground">
                Pedido: <strong>{invoiceInfo?.order_number}</strong>
              </p>

              {/* Billing type */}
              <div>
                <Label className="text-xs font-medium text-muted-foreground">Tipo de facturación</Label>
                <RadioGroup value={form.type} onValueChange={(v) => update('type', v)} className="flex gap-4 mt-2">
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="persona_fisica" id="inv-pf" data-testid="radio-inv-pf" />
                    <Label htmlFor="inv-pf" className="cursor-pointer text-sm">Persona física</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="empresa" id="inv-emp" data-testid="radio-inv-empresa" />
                    <Label htmlFor="inv-emp" className="cursor-pointer text-sm">Empresa</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Nombre o razón social *</Label>
                  <Input value={form.name} onChange={(e) => update('name', e.target.value)}
                    placeholder={form.type === 'empresa' ? 'Razón social' : 'Nombre completo'}
                    className="mt-1" data-testid="input-inv-name" />
                  <FieldError field="name" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">RFC * ({form.type === 'persona_fisica' ? '13 caracteres' : '12 caracteres'})</Label>
                  <Input value={form.rfc} onChange={(e) => update('rfc', e.target.value.toUpperCase())}
                    placeholder="Ej: XAXX010101000" className="mt-1 uppercase"
                    maxLength={form.type === 'persona_fisica' ? 13 : 12} data-testid="input-inv-rfc" />
                  <FieldError field="rfc" />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground">Correo electrónico para factura *</Label>
                <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  placeholder="facturacion@ejemplo.com" className="mt-1" data-testid="input-inv-email" />
                <FieldError field="email" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">País</Label>
                  <Input value={form.country} onChange={(e) => update('country', e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Estado *</Label>
                  {form.country === 'México' ? (
                    <Select value={form.state} onValueChange={(v) => update('state', v)}>
                      <SelectTrigger className="mt-1" data-testid="select-inv-state"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {MEXICAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={form.state} onChange={(e) => update('state', e.target.value)} className="mt-1" />
                  )}
                  <FieldError field="state" />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground">Calle y número *</Label>
                <Input value={form.street} onChange={(e) => update('street', e.target.value)}
                  placeholder="Ej: Av. Reforma 123" className="mt-1" data-testid="input-inv-street" />
                <FieldError field="street" />
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Colonia</Label>
                  <Input value={form.neighborhood} onChange={(e) => update('neighborhood', e.target.value)}
                    placeholder="Colonia" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">Ciudad *</Label>
                  <Input value={form.city} onChange={(e) => update('city', e.target.value)}
                    placeholder="Ciudad" className="mt-1" data-testid="input-inv-city" />
                  <FieldError field="city" />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground">C.P. *</Label>
                  <Input value={form.postal_code} onChange={(e) => update('postal_code', e.target.value.replace(/\D/g, ''))}
                    placeholder="00000" className="mt-1" maxLength={5} data-testid="input-inv-postal" />
                  <FieldError field="postal_code" />
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-muted-foreground">Uso de CFDI (opcional)</Label>
                <Select value={form.cfdi_use} onValueChange={(v) => update('cfdi_use', v)}>
                  <SelectTrigger className="mt-1" data-testid="select-inv-cfdi"><SelectValue placeholder="Selecciona (opcional)" /></SelectTrigger>
                  <SelectContent>
                    {CFDI_USES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full h-12 bg-accent hover:bg-accent/90 gap-2"
                data-testid="btn-submit-invoice"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Solicitar factura
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                La generación de facturas puede tardar hasta 24 horas hábiles.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default InvoiceRequest;
